
import { FormContainer, FormItem, Input, Select } from "components/ui";
import { Field } from "formik";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { HiOutlineInformationCircle } from "react-icons/hi";
import ScheduleDateTimePicker from "./ScheduleDateTimePicker";


export const RECURRENCE_OPTIONS_BASE = [
    { value: 'none', key: 'recurrence_none', fallback: 'No se repite' },
    { value: 'daily', key: 'recurrence_daily', fallback: 'Todos los días' },
    { value: 'weekly', key: 'recurrence_weekly', fallback: 'Cada semana' },
    { value: 'biweekly', key: 'recurrence_biweekly', fallback: 'Cada 15 días' },
    { value: 'monthly', key: 'recurrence_monthly', fallback: 'Cada mes' },
    { value: 'quarterly', key: 'recurrence_quarterly', fallback: 'Cada 3 meses' },
    { value: 'semiannual', key: 'recurrence_semiannual', fallback: 'Cada 6 meses' },
];

export const FREQUENCY_LABELS = {
    daily: 'diarios',
    weekly: 'semanales',
    biweekly: 'quincenales',
    monthly: 'mensuales',
    quarterly: 'trimestrales',
    semiannual: 'semestrales',
};

const INTERVALS = {
    daily: { unit: 'day', multiplier: 1 },
    weekly: { unit: 'week', multiplier: 1 },
    biweekly: { unit: 'week', multiplier: 2 },
    monthly: { unit: 'month', multiplier: 1 },
    quarterly: { unit: 'month', multiplier: 3 },
    semiannual: { unit: 'month', multiplier: 6 },
};

export function calculateEndTime(startTime, recurrence, count) {
    const start = dayjs(startTime);
    const interval = INTERVALS[recurrence];
    if (!interval || count < 1) return start.toDate();
    const amount = (count - 1) * interval.multiplier;
    return start.add(amount, interval.unit).toDate();
}

export function estimateEventCount(startTime, endTime, recurrence) {
    const start = dayjs(startTime);
    const end = dayjs(endTime);
    if (!start.isValid() || !end.isValid()) return 0;
    if (end.isBefore(start)) return 0;
    const interval = INTERVALS[recurrence];
    if (!interval) return 1;
    let count = 0;
    let cursor = start;
    while (!cursor.isAfter(end)) {
        count++;
        cursor = cursor.add(interval.multiplier, interval.unit);
    }
    return Math.max(count, 1);
}


const AddTicketSchedule = ({ errors, values, touched, defaultRecurrence }) => {

    const { t } = useTranslation()
    const endTimeRef = useRef(null)

    const recurrenceOptions = useMemo(() =>
        RECURRENCE_OPTIONS_BASE.map(o => ({
            value: o.value,
            label: t(o.key, { defaultValue: o.fallback || o.key }),
        })),
    [t]);

    const repeats = values.recurrence && values.recurrence !== 'none'
    const currentRecurrence = repeats ? values.recurrence : null

    const recurrenceError = touched?.recurrence && errors.recurrence
    const frequencyLabel = t('frequency', { defaultValue: 'Frecuencia' })

    const previewCount = useMemo(() => {
        if (!repeats || !values.start_time || !values.end_time || !currentRecurrence) return null;
        return estimateEventCount(values.start_time, values.end_time, currentRecurrence);
    }, [repeats, values.start_time, values.end_time, currentRecurrence]);

    return (
        <div>
            <h5 className="mb-4">{t('schedule_task', { defaultValue: 'Programar tarea' })}</h5>
            <FormContainer>
                {/* Titulo */}
                <FormItem
                    label={t('title')}
                    invalid={errors.title}
                    errorMessage={errors.title}
                >
                    <Field name="title">
                        {({ field, form }) => (
                            <Input
                                defaultValue={values.title}
                                placeholder={t('title')}
                                onChange={(ev) => {
                                    const value = ev.target.value?.length ? ev.target.value : null
                                    form.setFieldValue(field.name, value)
                                }}
                            />
                        )}
                    </Field>
                </FormItem>

                {/* Frecuencia */}
                <FormItem
                    label={frequencyLabel}
                    asterisk
                    invalid={Boolean(recurrenceError)}
                    errorMessage={recurrenceError}
                >
                    <Field name="recurrence">
                        {({ field, form }) => (
                            <Select
                                placeholder={frequencyLabel}
                                isSearchable={false}
                                options={recurrenceOptions}
                                value={recurrenceOptions.find(o => o.value === (values.recurrence || 'none'))}
                                onChange={(option) => {
                                    form.setFieldValue(field.name, option.value)
                                    if (option.value === 'none' && values.start_time) {
                                        const plusOneMin = new Date(new Date(values.start_time).getTime() + 60 * 1000)
                                        form.setFieldValue('end_time', plusOneMin)
                                    }
                                    if (option.value !== 'none') {
                                        setTimeout(() => {
                                            endTimeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                                        }, 50)
                                    }
                                }}
                                onMenuOpen={() => {
                                    setTimeout(() => {
                                        const menuEl = document.querySelector('.select__menu')
                                        if (menuEl) {
                                            menuEl.scrollIntoView({ behavior: 'smooth', block: 'end' })
                                        }
                                    }, 50)
                                }}
                            />
                        )}
                    </Field>
                </FormItem>

                {/* Desde y Hasta en grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-6">
                    {/* Desde */}
                    <FormItem
                        label={repeats ? t('since') : t('date_and_time', { defaultValue: 'Fecha y hora' })}
                        asterisk
                        invalid={touched?.start_time && errors.start_time}
                    >
                        <Field name="start_time">
                            {({ field, form }) => {
                                const hasError = touched?.start_time && errors.start_time
                                return (
                                    <div className="flex flex-col" data-error-field="start_time">
                                        <ScheduleDateTimePicker
                                            name={field.name}
                                            value={field.value ?? null}
                                            locale="es"
                                            placeholder={repeats ? t('since') : t('date_and_time', { defaultValue: 'Fecha y hora' })}
                                            inputFormat="DD/MM/YYYY [·] HH:mm"
                                            placement="top-start"
                                            className={`w-full ${hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                                            onChange={(value) => {
                                                form.setFieldValue(field.name, value ?? "")
                                                if (value && repeats) {
                                                    const endValue = form.values.end_time
                                                    if (!endValue || new Date(endValue) <= new Date(value)) {
                                                        const plusOneHour = new Date(new Date(value).getTime() + 60 * 60 * 1000)
                                                        form.setFieldValue('end_time', plusOneHour)
                                                    }
                                                }
                                                if (value && !repeats) {
                                                    const plusOneMin = new Date(new Date(value).getTime() + 60 * 1000)
                                                    form.setFieldValue('end_time', plusOneMin)
                                                }
                                            }}
                                            onBlur={() => {}}
                                        />
                                        {hasError && (
                                            <p className="mt-1 text-xs text-red-500">{errors.start_time}</p>
                                        )}
                                    </div>
                                )
                            }}
                        </Field>
                    </FormItem>

                    {/* Hasta — solo si se repite */}
                    {repeats && (
                        <FormItem
                            ref={endTimeRef}
                            label={t('until')}
                            asterisk
                            invalid={touched?.end_time && errors.end_time}
                        >
                            <Field name="end_time">
                                {({ field, form }) => {
                                    const hasError = touched?.end_time && errors.end_time
                                    const startDate = values.start_time ? new Date(values.start_time) : undefined
                                    return (
                                        <div data-error-field="end_time">
                                            <ScheduleDateTimePicker
                                                name={field.name}
                                                value={field.value ?? null}
                                                locale="es"
                                                placeholder={t('until')}
                                                inputFormat="DD/MM/YYYY [·] HH:mm"
                                                placement="top-start"
                                                minDate={startDate ? new Date(new Date(startDate).setHours(0, 0, 0, 0)) : undefined}
                                                minDateTime={startDate}
                                                className={`w-full ${hasError ? 'border-red-500' : ''}`}
                                                onChange={(value) => form.setFieldValue(field.name, value ?? "")}
                                                onBlur={() => {}}
                                            />
                                            {hasError && (
                                                <p className="mt-1 text-xs text-red-500">{errors.end_time}</p>
                                            )}
                                        </div>
                                    )
                                }}
                            </Field>
                        </FormItem>
                    )}
                </div>

                {/* Preview de cantidad de ejecuciones */}
                {previewCount !== null && previewCount > 0 && (
                    <p className={`text-xs flex items-center gap-1 ${previewCount <= 1 ? 'text-blue-500' : 'text-gray-400 dark:text-gray-500'}`}>
                        {previewCount <= 1 && <HiOutlineInformationCircle className="text-sm flex-shrink-0" />}
                        {previewCount <= 1
                            ? t('single_event_hint', { defaultValue: 'Solo se ejecutará 1 vez. Considere ampliar el rango.' })
                            : t('event_count_preview', { defaultValue: 'Se ejecutará {{count}} veces', count: previewCount })
                        }
                    </p>
                )}
            </FormContainer>
        </div>
    )
}

export default AddTicketSchedule
